/**
 * A numeric input that refuses what it cannot mean.
 *
 * Not `<input type="number">`: a Turkish keyboard produces a comma and the
 * native control rejects it, while still accepting `e`, `+` and `-` in most
 * browsers. So this is a text field with a decimal keypad on mobile that
 * filters keystrokes itself.
 *
 * Two separate jobs, and they are separate on purpose:
 *
 *   - A character that cannot appear in a number never lands in the field.
 *     Typing "abc" into Kilo used to leave the text there, parse to null and
 *     silently drop the value on save.
 *   - A number outside its bounds does land, and is reported under the field.
 *     Blocking the second digit of "111" as it is typed would look broken; the
 *     instructor needs to see what is wrong with what they wrote.
 *
 * The bounds come from @pp/shared so this and the Worker cannot disagree.
 */

import { useId } from 'react';
import {
  checkLimit,
  isPartialNumber,
  parseTypedNumber,
  type NumericLimit,
} from '@pp/shared';
import './NumberField.css';

export interface NumberFieldProps {
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  limit: NumericLimit;
  required?: boolean;
  /** Shown under the field when there is nothing wrong with it. */
  hint?: string;
  /** Sits beside the input, inside the field — a unit selector, typically. */
  suffix?: React.ReactNode;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
}

/** The message to show under a field, or null when it is fine. */
export function numberFieldError(value: string, limit: NumericLimit): string | null {
  const parsed = parseTypedNumber(value);
  if (parsed === undefined) return 'Sayı girin.';
  if (parsed === null) return null;
  return checkLimit(parsed, limit);
}

export function NumberField({
  label,
  value,
  onChange,
  limit,
  required = false,
  hint,
  suffix,
  className,
  placeholder,
  'aria-label': ariaLabel,
}: NumberFieldProps) {
  const id = useId();
  const error = numberFieldError(value, limit);

  return (
    <label className={className ? `numfield ${className}` : 'numfield'}>
      <span className="numfield__label">{label}</span>
      <span className="numfield__row">
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-invalid={error !== null}
          aria-describedby={error ? id : undefined}
          required={required}
          onChange={(e) => {
            // Anything that could not grow into a number is not accepted at
            // all, so the field never holds text the instructor must clear.
            if (isPartialNumber(e.target.value, limit)) onChange(e.target.value);
          }}
          onBlur={(e) => {
            // A lone separator is not a number; drop it rather than report it.
            if (e.target.value === ',' || e.target.value === '.') onChange('');
          }}
        />
        {suffix}
      </span>
      {error ? (
        <span className="numfield__error" id={id}>
          {error}
        </span>
      ) : (
        hint && <span className="numfield__hint">{hint}</span>
      )}
    </label>
  );
}
